import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "./firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ReportIssue.css";
import apnagharLogo from "./apnaghar.png";

export default function ReportIssue() {
  const navigate = useNavigate();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  const [activeTab, setActiveTab] = useState("listing");
  const [listingId, setListingId] = useState("");
  const [listingDesc, setListingDesc] = useState("");
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketDesc, setTicketDesc] = useState("");
  const [ticketName, setTicketName] = useState("");
  const [ticketUserId, setTicketUserId] = useState("");
  const [ticketCategory, setTicketCategory] = useState("General");
  const [ticketPriority, setTicketPriority] = useState("Normal");
  const [activeTickets, setActiveTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch user's username and set ticketUserId as UID
  useEffect(() => {
    if (!userId) return;

    const fetchUser = async () => {
      try {
        const userDoc = await getDoc(doc(db, "user-data", userId));
        if (userDoc.exists()) {
          setTicketUserId(userId); // UID as userId
          setTicketName(userDoc.data().username || "");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch user data");
      }
    };

    fetchUser();
  }, [userId]);

  // Fetch all tickets including resolved
const fetchActiveTickets = async () => {
  if (!ticketUserId) return;
  try {
    const q = query(collection(db, "tickets"), where("userId", "==", ticketUserId));
    const snapshot = await getDocs(q);
    const tickets = snapshot.docs
      .map((doc) => ({ docId: doc.id, ...doc.data() }))
      .filter((t) => t.status !== "closed"); // <-- filter out closed tickets
    setActiveTickets(tickets);
  } catch (err) {
    console.error(err);
    toast.error("Failed to fetch tickets");
  }
};

  useEffect(() => {
    fetchActiveTickets();
  }, [ticketUserId]);

  // Handle listing report submission
  const handleListingReport = async () => {
    if (!listingId || !listingDesc)
      return toast.error("Please enter Listing ID and Description.");

    const existing = activeTickets.find((t) => t.type === "listing" && t.status !== "resolved");
    if (existing) return toast.warn("You already have an active listing report.");

    setLoading(true);
    try {
      await addDoc(collection(db, "tickets"), {
        type: "listing",
        listingId,
        description: listingDesc,
        userId: ticketUserId,
        status: "pending",
        progress: 0,
        createdAt: serverTimestamp(),
      });
      toast.success("Listing report submitted!");
      setListingId("");
      setListingDesc("");
      fetchActiveTickets();
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit listing report");
    } finally {
      setLoading(false);
    }
  };

  // Handle support ticket creation
  const handleTicketCreate = async () => {
    if (!ticketName || !ticketUserId || !ticketSubject || !ticketDesc)
      return toast.error("Please fill in all fields.");

    const existing = activeTickets.find((t) => t.type === "ticket" && t.status !== "resolved");
    if (existing) return toast.warn("You already have an active support ticket.");

    setLoading(true);
    try {
      await addDoc(collection(db, "tickets"), {
        type: "ticket",
        userName: ticketName,
        userId: ticketUserId,
        category: ticketCategory,
        priority: ticketPriority,
        subject: ticketSubject,
        description: ticketDesc,
        status: "pending",
        progress: 0,
        createdAt: serverTimestamp(),
      });
      toast.success("Support ticket created!");
      setTicketSubject("");
      setTicketDesc("");
      setTicketCategory("General");
      setTicketPriority("Normal");
      fetchActiveTickets();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create support ticket");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div className="report-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <ToastContainer position="top-right" />

      <div className="report-card">
        <div className="report-header">
           <button className="backy" onClick={() => navigate("/homepage/profile")}>
      <span className="material-icons">arrow_back</span> Back
    </button>
          
          <div className="report-title">
            <img src={apnagharLogo} alt="Apnaghar Logo" className="report-logo" />
            <h2>Report Center</h2>
          </div>
        </div>

        <div className="tab-header">
          <button
            className={`tab-btn ${activeTab === "listing" ? "active" : ""}`}
            onClick={() => setActiveTab("listing")}
            disabled={loading}
          >
            Report a Listing
          </button>
          <button
            className={`tab-btn ${activeTab === "ticket" ? "active" : ""}`}
            onClick={() => setActiveTab("ticket")}
            disabled={loading}
          >
            Create a Ticket
          </button>
        </div>

        {/* Listing Report Form */}
        {activeTab === "listing" && (
          <motion.div key="listing" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="form-card">
            <h3>Report a problem with a listing</h3>
            <input className="form-input" placeholder="Enter Listing ID" value={listingId} onChange={(e) => setListingId(e.target.value)} />
            <textarea className="form-textarea" placeholder="Describe the issue clearly..." rows={4} value={listingDesc} onChange={(e) => setListingDesc(e.target.value)} />
            <button className="submit-btn blue" onClick={handleListingReport} disabled={loading}>
              Submit Listing Report
            </button>
          </motion.div>
        )}

        {/* Support Ticket Form */}
        {activeTab === "ticket" && (
          <motion.div key="ticket" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="form-card">
            <h3>Create a Support Ticket</h3>
            <div className="two-col">
              <input className="form-input" placeholder="Your Name" value={ticketName} onChange={(e) => setTicketName(e.target.value)} />
              <input className="form-input" placeholder="Your User ID (UID)" value={ticketUserId} disabled />
            </div>
            <div className="two-col">
              <select className="form-input" value={ticketCategory} onChange={(e) => setTicketCategory(e.target.value)}>
                <option>General</option>
                <option>Technical Issue</option>
                <option>Billing</option>
                <option>Account</option>
              </select>
              <select className="form-input" value={ticketPriority} onChange={(e) => setTicketPriority(e.target.value)}>
                <option>Normal</option>
                <option>High</option>
                <option>Urgent</option>
              </select>
            </div>
            <input className="form-input" placeholder="Enter Subject" value={ticketSubject} onChange={(e) => setTicketSubject(e.target.value)} />
            <textarea className="form-textarea" placeholder="Describe your issue..." rows={5} value={ticketDesc} onChange={(e) => setTicketDesc(e.target.value)} />
            <button className="submit-btn green" onClick={handleTicketCreate} disabled={loading}>
              Submit Support Ticket
            </button>
          </motion.div>
        )}

        {/* Active Tickets */}
        <motion.div className="report-card info-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h3>
            <span className="material-icons icon">pending_actions</span> Tickets
          </h3>
          {activeTickets.length === 0 ? (
            <p>No tickets.</p>
          ) : (
            <ul className="report-guidelines">
              {activeTickets.map((ticket) => (
                <li key={ticket.docId} style={{ marginBottom: "12px" }}>
                  <strong>{ticket.type === "listing" ? "Listing Report" : "Support Ticket"}:</strong>{" "}
                  {ticket.subject || ticket.listingId}
                  <span style={{ float: "right", color: ticket.status === "resolved" ? "#16a34a" : "#2563eb" }}>
                    {ticket.status}
                  </span>
                  {ticket.type === "ticket" && <p style={{ fontStyle: "italic", marginTop: "4px" }}>{ticket.description}</p>}
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: ticket.status === "pending" ? "33%" : ticket.status === "in-progress" ? "66%" : "100%",
                        backgroundColor: ticket.status === "resolved" ? "#16a34a" : "#2563eb",
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}


        

{/* --- Guidelines / Info Section --- */}
<div
  className="report-guidelines-info"
  style={{
    marginTop: "20px",
    padding: "10px",
    borderTop: "1px solid #ccc",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  }}
>
  <h4 style={{ marginBottom: "10px" }}>Important Information:</h4>
  <ul style={{ paddingLeft: "0", listStyle: "none" }}>
    <li style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
      <span className="material-icons" style={{ color: "#2563eb", marginRight: "8px" }}>schedule</span>
      Tickets are reviewed by our support team within 24 hours.
    </li>
    <li style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
      <span className="material-icons" style={{ color: "#2563eb", marginRight: "8px" }}>description</span>
      Make sure to provide clear descriptions for faster resolution.
    </li>
    <li style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
      <span className="material-icons" style={{ color: "#2563eb", marginRight: "8px" }}>priority_high</span>
      Only one active listing report or support ticket is allowed at a time.
    </li>
    <li style={{ display: "flex", alignItems: "center" }}>
      <span className="material-icons" style={{ color: "#16a34a", marginRight: "8px" }}>check_circle</span>
      Closed tickets will no longer appear in the active tickets list.
    </li>
  </ul>
</div>
</motion.div>
      </div>
    </motion.div>
  );
}
